import { Box, Skeleton, Text } from '@chakra-ui/react';
import type { Mention } from '@fluenceur/types';
import { MentionMediaType, MentionPlatform, MentionType } from '@fluenceur/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import MentionImageThumbnail from './MentionImageThumbnail';
import { MentionMediaUnavailable } from './MentionMediaUnavailable';
import { MentionVideoBadge } from './MentionVideoBadge';
import MentionVideoThumbnail from './MentionVideoThumbnail';
import { MentionSyncIcon } from './MentionSyncIcon';
import { useMentionThumbnailMedia } from './use-mention-thumbnail-media';

interface MentionMediaThumbnailProps {
  mention: Mention;
  width?: string;
  enabled?: boolean;
  isHovered?: boolean;
  openOnClick?: boolean;
  onError?: (error: Error) => void;
}

/**
 * Factory component for rendering mention media thumbnails
 */
export default function MentionMediaThumbnail({
  mention,
  width = '100%',
  enabled = true,
  isHovered = false,
  openOnClick = true,
  onError,
}: MentionMediaThumbnailProps) {
  const { t } = useTranslation();
  const {
    media: mediaData,
    isLoading,
    error,
    isUnavailable,
    onMediaError,
  } = useMentionThumbnailMedia({ mention, enabled });

  React.useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  if (isLoading) {
    return (
      <Box width={width} height="100%" rounded="none">
        <Skeleton height="100%" width="100%" />
      </Box>
    );
  }

  if (isUnavailable) {
    return <MentionMediaUnavailable expired={Boolean(mention.media?.isExpired)} width={width} />;
  }

  const hostedThumbnailUrl = mediaData?.hostedThumbnailUrl ?? null;
  const shouldUseHostedThumbnail =
    !!hostedThumbnailUrl && mention.mentionType === MentionType.STORY && mediaData?.isExpired;

  if (shouldUseHostedThumbnail) {
    return (
      <MentionImageThumbnail
        imageUrl={hostedThumbnailUrl}
        mention={mention}
        openOnClick={openOnClick}
        onError={onMediaError}
      />
    );
  }

  if (!mediaData?.mediaUrl) {
    // No file to draw: our own copy first, else the cover Meta serves for a reel
    // whose file it withholds, marked as a video so it does not read as a photo.
    const stillUrl = hostedThumbnailUrl ?? mediaData?.coverUrl ?? null;
    if (stillUrl) {
      return (
        <Box position="relative" width={width} height="100%">
          <MentionImageThumbnail
            imageUrl={stillUrl}
            mention={mention}
            openOnClick={openOnClick}
            onError={onMediaError}
          />
          {mediaData?.mediaType === MentionMediaType.VIDEO && <MentionVideoBadge />}
        </Box>
      );
    }

    return <MentionMediaUnavailable expired={Boolean(mediaData?.isExpired)} width={width} />;
  }

  const mediaUrl = mediaData.mediaUrl;

  const renderInner = () => {
    // TikTok only provides a thumbnail image, not a streamable video URL.
    // Display as image thumbnail for TikTok mentions regardless of mediaType.
    if (mention.platform === MentionPlatform.TIKTOK) {
      return (
        <MentionImageThumbnail
          imageUrl={mediaUrl}
          mention={mention}
          openOnClick={openOnClick}
          onError={onMediaError}
        />
      );
    }

    if (mediaData.mediaType === MentionMediaType.VIDEO) {
      return (
        <MentionVideoThumbnail
          videoUrl={mediaUrl}
          mention={mention}
          isHovered={isHovered}
          openOnClick={openOnClick}
          onError={onMediaError}
        />
      );
    }

    if (
      mediaData.mediaType === MentionMediaType.IMAGE ||
      mediaData.mediaType === MentionMediaType.CAROUSEL_ALBUM
    ) {
      return (
        <MentionImageThumbnail
          imageUrl={mediaUrl}
          mention={mention}
          openOnClick={openOnClick}
          onError={onMediaError}
        />
      );
    }

    return (
      <Box
        bg="gray.100"
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={width}
        height="100%"
        rounded="none"
      >
        <Text color="gray.500" fontSize="sm">
          {t('mentions.media.unsupported')}
        </Text>
      </Box>
    );
  };

  return (
    <Box role="group" position="relative" width={width} height="100%">
      {renderInner()}
      <MentionSyncIcon mentionId={mention.id} />
    </Box>
  );
}
